/**
 * Tiny pill that shows "REAL" when the user is signed in with their real
 * whitehatbots.com account. Renders nothing for demo sessions. Intentionally
 * small — the goal is a glance-able confirmation that the WHB handoff flow
 * succeeded, not a prominent status panel.
 *
 * The tooltip spells out the current caveat: the UI still renders mock data
 * because the data-layer wiring (whbFetch → proxy → WHB API) is next-phase
 * work. Remove the caveat when real-data consumers ship for all surfaces.
 */
import { Chip, Tooltip } from "@mui/material";
import VerifiedOutlined from "@mui/icons-material/VerifiedOutlined";
import { useAuthStore } from "@/store/auth-store";

export function RealSessionBadge({
  compact = false,
}: {
  /** Hide the text label; show just the verified icon. Useful on tight bars. */
  compact?: boolean;
}) {
  const kind = useAuthStore((s) => s.kind);
  if (kind !== "real") return null;

  return (
    <Tooltip
      title="Signed in with your whitehatbots.com account. UI is still rendering mock data — real account data will light up as each page is wired to the WHB API."
      placement="bottom"
    >
      <Chip
        size="small"
        color="success"
        variant="outlined"
        icon={<VerifiedOutlined sx={{ fontSize: 16 }} />}
        label={compact ? undefined : "REAL"}
        sx={{
          height: 24,
          fontWeight: 700,
          letterSpacing: 0.5,
          fontSize: "0.7rem",
          px: compact ? 0 : 0.5,
          "& .MuiChip-label": compact
            ? { display: "none" }
            : { px: 0.75 },
          "& .MuiChip-icon": { ml: compact ? 0.5 : 0.75, mr: compact ? 0.5 : -0.25 },
        }}
      />
    </Tooltip>
  );
}
